import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Calendar, Clock, X, CheckCircle2, Users, MapPin, Video, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_SLOTS = [
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '02:00 PM',
  '03:00 PM',
  '04:00 PM',
  '05:00 PM',
];

const MEETING_TYPES = [
  { value: 'Site Visit', icon: MapPin, label: 'Site Visit' },
  { value: 'Office Meeting', icon: Briefcase, label: 'Office Meeting' },
  { value: 'Video Call', icon: Video, label: 'Video Call' },
];

// Returns YYYY-MM-DD for "today + offset", as a local date string
const dateStr = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isSunday = (yyyyMmDd) => {
  // Local interpretation
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
};

export const AppointmentModal = ({ isOpen, onClose, property = null, prefill = null, meetingType = null, title = null }) => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    date: dateStr(1),
    time_slot: TIME_SLOTS[0],
    visitors: 1,
    meeting_type: meetingType || 'Site Visit',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);       // full-capacity slots
  const [partialSlots, setPartialSlots] = useState([]);     // 1..cap-1 taken
  const [slotCounts, setSlotCounts] = useState({});         // { "10:00 AM": 2 }
  const [slotCap, setSlotCap] = useState(2);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Fetch already-booked slots for the currently-selected date so buyers see
  // what's taken before picking a time.
  useEffect(() => {
    if (!isOpen || !form.date) return;
    let cancelled = false;
    setLoadingSlots(true);
    axios.get(`${API}/appointments/booked-slots`, { params: { date: form.date } })
      .then(res => {
        if (cancelled) return;
        setBookedSlots(res.data?.booked_slots || []);
        setPartialSlots(res.data?.partially_booked || []);
        setSlotCounts(res.data?.slot_counts || {});
        setSlotCap(res.data?.cap || 2);
      })
      .catch(() => {
        if (!cancelled) {
          setBookedSlots([]);
          setPartialSlots([]);
          setSlotCounts({});
        }
      })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [isOpen, form.date]);

  // Auto-advance: if the currently-selected slot is full, jump the dropdown
  // to the first still-open slot so the user does not have to hunt.
  useEffect(() => {
    if (!isOpen || loadingSlots) return;
    if (!bookedSlots.includes(form.time_slot)) return;
    const nextOpen = TIME_SLOTS.find((t) => !bookedSlots.includes(t));
    if (nextOpen && nextOpen !== form.time_slot) {
      setForm((f) => ({ ...f, time_slot: nextOpen }));
    }
  }, [bookedSlots, loadingSlots, isOpen, form.time_slot]);

  // Whenever the modal opens (and any time the pre-fill payload changes), seed
  // the form so callers like the Land-Fill estimator hand off name/phone/email
  // and users only pick date + time.
  useEffect(() => {
    if (isOpen) {
      setSuccess(null);
      document.body.style.overflow = 'hidden';
      setForm((f) => ({
        ...f,
        name:  prefill?.name  ?? f.name,
        phone: prefill?.phone ?? f.phone,
        email: prefill?.email ?? f.email,
        notes: prefill?.notes ?? f.notes,
        meeting_type: meetingType || f.meeting_type || 'Site Visit',
      }));
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, prefill, meetingType]);

  if (!isOpen) return null;

  const minDate = dateStr(1); // earliest = tomorrow
  const maxDate = dateStr(60); // 2 months ahead
  const sundayChosen = isSunday(form.date);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sundayChosen) {
      alert('We are closed on Sundays. Please pick another day.');
      return;
    }
    if (form.phone.replace(/\D/g, '').length < 10) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        phone: form.phone.replace(/\D/g, ''),
        visitors: Number(form.visitors) || 1,
        property_id: property?.id || null,
        property_title: property?.title || null,
        origin: window.location.origin,
      };
      const res = await axios.post(`${API}/appointments`, payload);
      setSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not book the appointment. Please try again.';
      alert(`❌ ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!form.name || !form.phone || !form.date || !form.time_slot) {
      alert('Please fill your name, phone, date and slot first.');
      return;
    }
    if (form.phone.replace(/\D/g, '').length < 10) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone.replace(/\D/g, ''),
        email: form.email || null,
        date: form.date,
        time_slot: form.time_slot,
        property_id: property?.id || null,
        property_title: property?.title || null,
      };
      const res = await axios.post(`${API}/appointments/waitlist`, payload);
      setSuccess({
        _waitlist: true,
        message: res.data?.message || 'Added to waitlist.',
        position: res.data?.position,
        time_slot: form.time_slot,
        date: form.date,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not join the waitlist. Please try again.';
      alert(`❌ ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Success / confirmation view
  if (success) {
    return createPortal(
      <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4 animate-fadeIn" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div className="bg-white rounded-2xl max-w-md w-full p-8 relative max-h-[92vh] overflow-y-auto">
          <button
            onClick={() => {
              setSuccess(null);
              onClose();
            }}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            data-testid="appointment-confirm-close"
          >
            <X size={20} />
          </button>
          <div className="text-center">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
              success._waitlist ? 'bg-amber-100' : 'bg-emerald-100'
            }`}>
              <CheckCircle2 size={36} className={success._waitlist ? 'text-amber-700' : 'text-emerald-700'} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {success._waitlist ? "You're on the waitlist!" : 'Appointment Booked!'}
            </h2>
            <p className="text-slate-600 mb-6">
              {success._waitlist
                ? `${success.message} We'll WhatsApp you the moment the ${success.time_slot} slot opens up.`
                : `Thank you, ${success.name}. We have received your appointment request.`}
            </p>
            <div className="bg-slate-50 rounded-xl p-5 text-left space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <Calendar size={18} className="text-emerald-700 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500">Date</div>
                  <div className="text-sm font-medium text-slate-900">{success.date}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock size={18} className="text-emerald-700 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500">Time Slot</div>
                  <div className="text-sm font-medium text-slate-900">{success.time_slot}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Users size={18} className="text-emerald-700 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500">Visitors · Type</div>
                  <div className="text-sm font-medium text-slate-900">
                    {success.visitors} · {success.meeting_type}
                  </div>
                </div>
              </div>
              {success.property_title && (
                <div className="flex items-start gap-2">
                  <MapPin size={18} className="text-emerald-700 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500">Property</div>
                    <div className="text-sm font-medium text-slate-900">{success.property_title}</div>
                  </div>
                </div>
              )}
              <div className="pt-3 border-t border-slate-200 text-xs text-slate-500">
                Confirmation ID:&nbsp;
                <span className="font-mono text-slate-700">{success.id.slice(0, 8)}</span>
                <br />
                Status:&nbsp;
                <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">
                  {success.status}
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Our team will call you on +91 {success.phone} to confirm.
            </p>
            <Button
              onClick={() => {
                setSuccess(null);
                onClose();
              }}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="appointment-done-btn"
            >
              Done
            </Button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4 animate-fadeIn" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 relative max-h-[92vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          data-testid="appointment-close"
        >
          <X size={22} />
        </button>

        <div className="mb-6">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
            <Calendar size={22} className="text-emerald-700" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">{title || 'Book an Appointment'}</h2>
          <p className="text-slate-600 text-sm mt-1">
            {property
              ? `For: ${property.title}`
              : 'Site visit, office meeting, or video call — pick what suits you.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Your name"
                data-testid="appt-name"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={form.phone}
                onChange={(e) =>
                  setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })
                }
                required
                placeholder="10-digit mobile"
                data-testid="appt-phone"
              />
            </div>
          </div>

          <div>
            <Label>Email (optional)</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              data-testid="appt-email"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1.5">
                <Calendar size={14} className="text-emerald-700" /> Date *
              </Label>
              <Input
                type="date"
                value={form.date}
                min={minDate}
                max={maxDate}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                data-testid="appt-date"
              />
              {sundayChosen && (
                <p className="text-xs text-red-600 mt-1">We are closed on Sundays.</p>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <Clock size={14} className="text-emerald-700" /> Time Slot *
              </Label>
              <Select
                value={form.time_slot}
                onValueChange={(v) => setForm({ ...form, time_slot: v })}
              >
                <SelectTrigger data-testid="appt-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[1100]">
                  {TIME_SLOTS.map((t) => {
                    const full = bookedSlots.includes(t);
                    const partial = partialSlots.includes(t);
                    const takenCount = slotCounts[t] || 0;
                    return (
                      <SelectItem key={t} value={t} disabled={full}>
                        {t}
                        {full ? '  •  FULL' : partial ? `  •  ${takenCount}/${slotCap} taken` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {/* Booked-slots preview for the selected date */}
              <div className="mt-2" data-testid="booked-slots-strip">
                {loadingSlots ? (
                  <div className="text-[10px] text-slate-500">Checking slot availability…</div>
                ) : bookedSlots.length === 0 && partialSlots.length === 0 ? (
                  <div className="text-[10px] font-semibold text-emerald-700">
                    ✅ All slots open on this day
                  </div>
                ) : (
                  <div className="flex items-center gap-1 flex-wrap">
                    {bookedSlots.length > 0 && (
                      <>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                          Full:
                        </span>
                        {bookedSlots.map((t) => (
                          <span
                            key={`full-${t}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200 line-through"
                            data-testid={`booked-slot-${t.replace(/[^0-9A-Za-z]/g, '')}`}
                          >
                            {t}
                          </span>
                        ))}
                      </>
                    )}
                    {partialSlots.length > 0 && (
                      <>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-1">
                          Partly:
                        </span>
                        {partialSlots.map((t) => {
                          const c = slotCounts[t] || 0;
                          return (
                            <span
                              key={`partial-${t}`}
                              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
                              data-testid={`partial-slot-${t.replace(/[^0-9A-Za-z]/g, '')}`}
                            >
                              {t} · {c}/{slotCap}
                            </span>
                          );
                        })}
                      </>
                    )}
                    <span className="text-[10px] text-slate-500 ml-1">
                      · {TIME_SLOTS.filter((t) => !bookedSlots.includes(t)).length} still open
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1.5 mb-1.5">
                <Users size={14} className="text-emerald-700" /> Number of Visitors
              </Label>
              <div
                className="flex items-center justify-between border border-input rounded-md h-10 px-2 bg-white"
                data-testid="appt-visitors-wrapper"
              >
                <button
                  type="button"
                  onClick={() => setForm({ ...form, visitors: Math.max(1, (Number(form.visitors) || 1) - 1) })}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-emerald-100 text-slate-700 font-bold text-lg leading-none flex items-center justify-center transition-colors disabled:opacity-40"
                  disabled={(Number(form.visitors) || 1) <= 1}
                  aria-label="Decrease visitors"
                  data-testid="appt-visitors-dec"
                >
                  −
                </button>
                <span
                  className="text-base font-semibold text-slate-900 tabular-nums min-w-[1.5rem] text-center"
                  data-testid="appt-visitors"
                >
                  {Number(form.visitors) || 1}
                </span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, visitors: Math.min(10, (Number(form.visitors) || 1) + 1) })}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-emerald-100 text-slate-700 font-bold text-lg leading-none flex items-center justify-center transition-colors disabled:opacity-40"
                  disabled={(Number(form.visitors) || 1) >= 10}
                  aria-label="Increase visitors"
                  data-testid="appt-visitors-inc"
                >
                  +
                </button>
              </div>
            </div>
            <div>
              <Label>Meeting Type</Label>
              <Select
                value={form.meeting_type}
                onValueChange={(v) => setForm({ ...form, meeting_type: v })}
              >
                <SelectTrigger data-testid="appt-meeting-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[1100]">
                  {MEETING_TYPES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Anything you'd like us to know in advance?"
              data-testid="appt-notes"
            />
          </div>

          {bookedSlots.includes(form.time_slot) && (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-3"
              data-testid="waitlist-banner"
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                <Clock size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-black text-amber-900">
                  {form.time_slot} on {form.date} is fully booked
                </div>
                <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
                  Join the waitlist and we&apos;ll WhatsApp you the moment a slot opens.
                  You keep your spot in line if the earlier client cancels.
                </p>
                <button
                  type="button"
                  onClick={handleJoinWaitlist}
                  disabled={submitting}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 rounded-full px-3 py-1.5 shadow-sm hover:shadow transition-all disabled:opacity-60"
                  data-testid="waitlist-join-btn"
                >
                  {submitting ? 'Joining…' : 'Join waitlist'}
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
              disabled={submitting || sundayChosen || bookedSlots.includes(form.time_slot)}
              data-testid="appt-submit"
            >
              {submitting ? 'Booking…' : bookedSlots.includes(form.time_slot) ? 'Slot full' : 'Confirm Booking'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AppointmentModal;
